import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Paper,
  Skeleton,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import RestoreFromTrashRoundedIcon from "@mui/icons-material/RestoreFromTrashRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import StarOutlineRoundedIcon from "@mui/icons-material/StarOutlineRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import InsertLinkRoundedIcon from "@mui/icons-material/InsertLinkRounded";
import ContentPasteGoRoundedIcon from "@mui/icons-material/ContentPasteGoRounded";
import AutoFixHighRoundedIcon from "@mui/icons-material/AutoFixHighRounded";
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import TranslateRoundedIcon from "@mui/icons-material/TranslateRounded";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import StyleOutlinedIcon from "@mui/icons-material/StyleOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getMyStores, getMyStoreCategories } from "../../api/storeService";
import { getCategories, getCategoryAttributes } from "../../api/categoryService";
import { createCustomAttributeOption } from "../../api/productAttributeService";
import AttributeOptionPicker, { getAttributeLabel } from "../../components/AttributeOptionPicker";
import { removeImageBackground } from "../../api/imageService";
import { translateToArabicAndHebrew } from "../../api/translationService";
import {
  getMyProducts,
  getDeletedProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  restoreProduct,
  deleteProductPermanently,
  uploadProductImage,
  addProductImageFromUrl,
  removeProductImageBackground,
  replaceProductImage,
  deleteProductImage,
  setMainProductImage,
  uploadProductVideo,
  deleteProductVideo,
  createProductVariant,
  updateProductVariant,
  deleteProductVariant,
  uploadVariantImage,
  addVariantImageFromUrl,
  removeVariantImageBackground,
  deleteVariantImage,
  getAuthenticityOptions,
} from "../../api/productService";
import { EmptyState } from "../Admin/analyticsShared";
import { formatCurrency } from "../Admin/constants";
import { pickLocalizedName } from "../../utils/localizedName";

// Numeric values must match the backend's ProductDiscountType enum (Models/Enums.cs).
const DISCOUNT_TYPE_NONE = 0;
const DISCOUNT_TYPE_FIXED = 1;
const DISCOUNT_TYPE_PERCENTAGE = 2;

// Matches the backend's AttributeInputType enum.
const ATTRIBUTE_INPUT_DROPDOWN = 1;
const ATTRIBUTE_INPUT_NUMBER = 3;

// Matches the backend's ProductVideoPlacement enum.
const VIDEO_PLACEMENT_VIDEO_FIRST = 1;
const VIDEO_PLACEMENT_MAIN_THEN_VIDEO = 2;
const VIDEO_PLACEMENT_PHOTOS_FIRST = 3;
const VIDEO_PLACEMENT_OPTIONS = [
  { value: VIDEO_PLACEMENT_VIDEO_FIRST, labelKey: "boProducts:videoPlacementVideoFirst" },
  { value: VIDEO_PLACEMENT_MAIN_THEN_VIDEO, labelKey: "boProducts:videoPlacementMainThenVideo" },
  { value: VIDEO_PLACEMENT_PHOTOS_FIRST, labelKey: "boProducts:videoPlacementPhotosFirst" },
];

const MAX_IMAGES = 8;
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;
const ALLOWED_VIDEO_TYPES = ["video/mp4", "video/quicktime"];
const MAX_VIDEO_SIZE_BYTES = 75 * 1024 * 1024;

const emptyVariantForm = {
  variantName: "",
  price: "",
  stockQuantity: "0",
  sku: "",
  discountType: DISCOUNT_TYPE_NONE,
  discountPrice: "",
  discountPercent: "",
  attributeValues: {},
  imageFile: null,
  imageUrl: null,
  imagePreviewUrl: null,
  // Saved variant photo whose background the server should remove on save.
  removeBackground: false,
};

// Builds the CreateProductVariantRequest/UpdateProductVariantRequest `attributeValues` list.
// Variant-generating attributes are always dropdowns, so the value is just an option id.
function buildVariantAttributeValuesPayload(variantAttrs, optionIdByAttributeId) {
  return variantAttrs
    .map((attr) => {
      const optionId = optionIdByAttributeId[attr.attributeId];
      return optionId != null ? { attributeId: attr.attributeId, optionId } : null;
    })
    .filter(Boolean);
}

// Products can only be assigned to a leaf category (no subcategories) that the
// store has already linked via its store-categories - mirrors the backend's
// ValidateProductCategoryAsync check.
function buildCategoryOptions(categories, storeCategories, language) {
  const parentIds = new Set();
  const byId = new Map();
  categories.forEach((c) => {
    byId.set(c.categoryId, c);
    if (c.parentCategoryId != null) parentIds.add(c.parentCategoryId);
  });

  const pathFor = (categoryId) => {
    const parts = [];
    let current = byId.get(categoryId);
    while (current) {
      parts.unshift(pickLocalizedName(language, current.name, current.nameArabic, current.nameHebrew));
      current = current.parentCategoryId != null ? byId.get(current.parentCategoryId) : null;
    }
    return parts.join(" › ");
  };

  return storeCategories
    .filter((sc) => !parentIds.has(sc.categoryId))
    .map((sc) => ({ categoryId: sc.categoryId, name: pathFor(sc.categoryId) }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

// Reads image bytes off the OS clipboard as Files - used for both the explicit
// "Paste image" button (navigator.clipboard.read(), needs a user-gesture) and
// the global Ctrl+V handler below (clipboardData.items from the paste event).
async function readImageFilesFromClipboard() {
  const clipboardItems = await navigator.clipboard.read();
  const files = [];
  for (const item of clipboardItems) {
    const imageType = item.types.find((type) => type.startsWith("image/"));
    if (!imageType) continue;
    // eslint-disable-next-line no-await-in-loop
    const blob = await item.getType(imageType);
    const ext = imageType.split("/")[1] || "png";
    files.push(new File([blob], `pasted-${Date.now()}.${ext}`, { type: imageType }));
  }
  return files;
}

function imageFilesFromClipboardEvent(e) {
  const items = e.clipboardData?.items;
  if (!items) return [];
  const files = [];
  for (const item of items) {
    if (item.kind === "file" && item.type.startsWith("image/")) {
      const file = item.getAsFile();
      if (file) files.push(file);
    }
  }
  return files;
}

function resolveDiscountFields(discountType, discountPriceText, discountPercentText, price, t) {
  if (discountType === DISCOUNT_TYPE_FIXED) {
    const parsed = parseFloat(discountPriceText);
    if (Number.isNaN(parsed) || parsed <= 0 || parsed >= price) {
      return { error: t("boProducts:discountPriceError") };
    }
    return { discountType, discountPrice: parsed, discountPercent: null };
  }
  if (discountType === DISCOUNT_TYPE_PERCENTAGE) {
    const parsed = parseFloat(discountPercentText);
    if (Number.isNaN(parsed) || parsed <= 0 || parsed >= 100) {
      return { error: t("boProducts:discountPercentError") };
    }
    return { discountType, discountPrice: null, discountPercent: parsed };
  }
  return { discountType: DISCOUNT_TYPE_NONE, discountPrice: null, discountPercent: null };
}

// Small dialog for pasting a direct image URL instead of uploading a file - shared
// between the main product photos and a variant's photo.
function AddImageByUrlDialog({ open, onClose, onAdd, busy, error }) {
  const { t } = useTranslation(["boProducts", "boShared", "common"]);
  const [url, setUrl] = useState("");

  useEffect(() => {
    if (open) setUrl("");
  }, [open]);

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t("boProducts:addFromLinkTitle")}</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label={t("boProducts:addFromLinkLabel")}
            placeholder="https://example.com/photo.jpg"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            autoFocus
            fullWidth
            disabled={busy}
          />
          <Typography variant="caption" color="text.secondary">
            {t("boProducts:addFromLinkHint")}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t("common:cancel")}
        </Button>
        <Button variant="contained" onClick={() => onAdd(url.trim())} disabled={busy || !url.trim()}>
          {busy ? t("boShared:saving") : t("common:add")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function Products() {
  const { t } = useTranslation(["boProducts", "boSidebar", "boShared", "common", "boStores", "boProductDiscounts"]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState(null);
  const [loadingStores, setLoadingStores] = useState(true);

  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busyProductId, setBusyProductId] = useState(null);

  const selectedStore = useMemo(() => stores.find((s) => s.storeId === storeId) || null, [stores, storeId]);

  useEffect(() => {
    let active = true;
    getMyStores()
      .then((data) => {
        if (!active) return;
        setStores(data);
        // ?storeId= keeps the chosen store when coming back from the Discounts page.
        const requestedId = Number(searchParams.get("storeId"));
        const requested = data.find((s) => s.storeId === requestedId);
        if (data.length > 0) setStoreId(requested ? requested.storeId : data[0].storeId);
      })
      .catch(() => {
        if (active) setError(t("boStores:loadError"));
      })
      .finally(() => {
        if (active) setLoadingStores(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function reloadProducts() {
    if (!storeId) return;
    setLoadingProducts(true);
    setError("");
    const request = showDeleted ? getDeletedProducts(storeId) : getMyProducts(storeId);
    request
      .then(setProducts)
      .catch(() => setError(t("boProducts:loadError")))
      .finally(() => setLoadingProducts(false));
  }

  useEffect(() => {
    reloadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, showDeleted]);

  const filteredProducts = useMemo(() => {
    const term = search.trim().toLowerCase();
    const list = term ? products.filter((p) => p.name.toLowerCase().includes(term)) : products;
    return [...list].sort((a, b) => (a.categoryName || "").localeCompare(b.categoryName || "") || a.name.localeCompare(b.name));
  }, [products, search]);

  function openCreateDialog() {
    setEditingProduct(null);
    setDialogOpen(true);
  }

  function openEditDialog(product) {
    setEditingProduct(product);
    setDialogOpen(true);
  }

  function handleSaved(message) {
    setDialogOpen(false);
    setEditingProduct(null);
    setNotice(message);
    reloadProducts();
  }

  async function handleDelete() {
    const product = deleteTarget;
    setBusyProductId(product.productId);
    try {
      await deleteProduct(product.productId);
      setDeleteTarget(null);
      setNotice(t("boProducts:productDeleted", { name: product.name }));
      reloadProducts();
    } catch {
      setError(t("boProducts:deleteError"));
    } finally {
      setBusyProductId(null);
    }
  }

  async function handleRestore(product) {
    setBusyProductId(product.productId);
    setError("");
    try {
      await restoreProduct(product.productId);
      setNotice(t("boProducts:productRestored", { name: product.name }));
      reloadProducts();
    } catch {
      setError(t("boProducts:restoreError"));
    } finally {
      setBusyProductId(null);
    }
  }

  async function handlePermanentDelete(product) {
    setBusyProductId(product.productId);
    setError("");
    try {
      await deleteProductPermanently(product.productId);
      setNotice(t("boProducts:productPermanentlyDeleted", { name: product.name }));
      reloadProducts();
    } catch (err) {
      setError(err.response?.data || t("boProducts:permanentDeleteError"));
    } finally {
      setBusyProductId(null);
    }
  }

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{ mb: 3, justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" } }}
      >
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <Avatar sx={{ bgcolor: "primary.main", width: 44, height: 44 }}>
            <Inventory2RoundedIcon />
          </Avatar>
          <Box>
            <Typography variant="h4">{t("boSidebar:products")}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t("boProducts:subtitle")}
            </Typography>
          </Box>
        </Stack>

        {!showDeleted && (
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<LocalOfferOutlinedIcon />}
              onClick={() => navigate(`/business-owner/products/discounts?storeId=${storeId}`)}
              disabled={!storeId}
            >
              {t("boProductDiscounts:openButton")}
            </Button>
            <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreateDialog} disabled={!storeId}>
              {t("boProducts:addProduct")}
            </Button>
          </Stack>
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {typeof error === "string" ? error : t("boShared:somethingWentWrong")}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2.5, alignItems: { xs: "stretch", sm: "center" } }}>
        {stores.length > 1 && (
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>{t("boShared:storeFallback")}</InputLabel>
            <Select label={t("boShared:storeFallback")} value={storeId ?? ""} onChange={(e) => setStoreId(e.target.value)}>
              {stores.map((s) => (
                <MenuItem key={s.storeId} value={s.storeId}>
                  {s.storeName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <TextField
          placeholder={t("boProducts:searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: 260, flexGrow: 1 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")}>
                    <CloseRoundedIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />

        <Button
          variant={showDeleted ? "contained" : "outlined"}
          color={showDeleted ? "secondary" : "inherit"}
          startIcon={showDeleted ? <Inventory2RoundedIcon /> : <DeleteOutlineRoundedIcon />}
          onClick={() => setShowDeleted((prev) => !prev)}
        >
          {showDeleted ? t("boProducts:backToProducts") : t("boProducts:deletedProducts")}
        </Button>
      </Stack>

      {loadingStores || loadingProducts ? (
        <Grid container spacing={2}>
          {[1, 2, 3, 4].map((i) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={i}>
              <Skeleton variant="rounded" height={260} />
            </Grid>
          ))}
        </Grid>
      ) : !storeId ? (
        <EmptyState icon={Inventory2RoundedIcon} text={t("boProducts:noStores")} />
      ) : filteredProducts.length === 0 ? (
        <EmptyState
          icon={showDeleted ? DeleteOutlineRoundedIcon : Inventory2RoundedIcon}
          text={showDeleted ? t("boProducts:noDeletedProducts") : t("boProducts:noProducts")}
        />
      ) : (
        <Grid container spacing={2}>
          {filteredProducts.map((product) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={product.productId}>
              <ProductCard
                product={product}
                busy={busyProductId === product.productId}
                showDeleted={showDeleted}
                onEdit={() => openEditDialog(product)}
                onDelete={() => setDeleteTarget(product)}
                onRestore={() => handleRestore(product)}
                onPermanentDelete={() => handlePermanentDelete(product)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {dialogOpen && (
        <ProductFormDialog
          store={selectedStore}
          product={editingProduct}
          onClose={() => {
            setDialogOpen(false);
            setEditingProduct(null);
          }}
          onSaved={handleSaved}
        />
      )}

      {deleteTarget && (
        <Dialog open onClose={() => setDeleteTarget(null)} fullWidth maxWidth="xs">
          <DialogTitle>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: "error.main", width: 36, height: 36 }}>
                <WarningAmberRoundedIcon fontSize="small" />
              </Avatar>
              <span>{t("boProducts:deleteConfirmTitle")}</span>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              {t("boProducts:deleteConfirmBody", { name: deleteTarget.name })}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteTarget(null)}>{t("common:cancel")}</Button>
            <Button variant="contained" color="error" onClick={handleDelete}>
              {t("common:delete")}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}

function ProductCard({ product, busy, showDeleted, onEdit, onDelete, onRestore, onPermanentDelete }) {
  const { t } = useTranslation(["boProducts", "boShared", "common"]);
  const mainImage = product.images?.find((i) => i.isMain)?.imageUrl || product.images?.[0]?.imageUrl || null;

  return (
    <Card
      variant="outlined"
      sx={{ height: "100%", display: "flex", flexDirection: "column", opacity: busy ? 0.6 : 1, position: "relative" }}
    >
      <Box
        sx={{
          aspectRatio: "1 / 1",
          bgcolor: "background.default",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderBottom: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        {mainImage ? (
          <Box component="img" src={mainImage} alt={product.name} sx={{ width: "100%", height: "100%", objectFit: "contain" }} />
        ) : (
          <Inventory2RoundedIcon sx={{ fontSize: 40, color: "text.disabled" }} />
        )}
      </Box>

      <Box sx={{ p: 2, flexGrow: 1, display: "flex", flexDirection: "column" }}>
        <Stack direction="row" spacing={0.75} sx={{ mb: 1, flexWrap: "wrap", gap: 0.5 }}>
          {product.categoryName && <Chip size="small" label={product.categoryName} variant="outlined" />}
          {product.authenticityValue && (
            <Chip size="small" label={product.authenticityValue} color="primary" variant="outlined" />
          )}
          {!product.isActive && !showDeleted && <Chip size="small" label={t("boShared:inactive")} color="default" />}
          {product.approvalStatus === 2 && !showDeleted && (
            <Chip size="small" color="warning" icon={<HourglassTopRoundedIcon />} label={t("boProducts:underReview")} />
          )}
          {product.approvalStatus === 3 && !showDeleted && (
            <Tooltip title={product.approvalRejectionReason || ""}>
              <Chip size="small" color="error" label={t("boProducts:rejected")} />
            </Tooltip>
          )}
        </Stack>

        <Typography variant="subtitle1" fontWeight={700} noWrap title={product.name}>
          {product.name}
        </Typography>

        <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mt: 0.5, mb: 0.5 }}>
          {product.discountPrice != null ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ textDecoration: "line-through" }}>
                {formatCurrency(product.price)}
              </Typography>
              <Typography variant="subtitle2" color="error.main" fontWeight={700}>
                {formatCurrency(product.discountPrice)}
              </Typography>
            </>
          ) : (
            <Typography variant="subtitle2" fontWeight={700}>
              {formatCurrency(product.price)}
            </Typography>
          )}
        </Stack>

        <Typography variant="caption" color="text.secondary">
          {product.stockQuantity > 0 ? t("boProducts:inStockCount", { count: product.stockQuantity }) : t("common:outOfStock")}
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        <Divider sx={{ my: 1.25 }} />

        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          {showDeleted ? (
            <>
              <Tooltip title={t("boProducts:restore")}>
                <IconButton size="small" onClick={onRestore} disabled={busy}>
                  <RestoreFromTrashRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("boProducts:deletePermanently")}>
                <IconButton size="small" color="error" onClick={onPermanentDelete} disabled={busy}>
                  <DeleteForeverRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <>
              <Tooltip title={t("common:edit")}>
                <IconButton size="small" onClick={onEdit} disabled={busy}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("common:delete")}>
                <IconButton size="small" onClick={onDelete} disabled={busy}>
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Stack>
      </Box>
    </Card>
  );
}

// One titled card in the product form (Basic information, Media, Pricing, ...).
function FormSection({ icon: Icon, title, subtitle, action, children }) {
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2.5 }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: children ? 2 : 0 }}>
        {Icon && (
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 1.5,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "primary.main",
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
            }}
          >
            <Icon sx={{ fontSize: 19 }} />
          </Box>
        )}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
      </Stack>
      {children}
    </Paper>
  );
}

const MEDIA_TILE_SIZE = 104;

const mediaTileSx = {
  position: "relative",
  width: MEDIA_TILE_SIZE,
  height: MEDIA_TILE_SIZE,
  borderRadius: 2,
  overflow: "hidden",
  border: "1px solid",
  borderColor: "divider",
  bgcolor: "background.default",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const mediaTileBusySx = {
  position: "absolute",
  inset: 0,
  bgcolor: "rgba(0,0,0,0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 2,
};

// Always-visible action strip along the bottom of a photo tile (works on touch, unlike hover menus).
const mediaTileActionsSx = {
  position: "absolute",
  insetInline: 0,
  bottom: 0,
  zIndex: 1,
  display: "flex",
  justifyContent: "center",
  gap: 0.25,
  py: 0.25,
  bgcolor: "rgba(0,0,0,0.55)",
  "& .MuiIconButton-root": { color: "#fff", p: 0.4, "&:hover": { bgcolor: "rgba(255,255,255,0.15)" } },
};

const mainPhotoBadgeSx = {
  position: "absolute",
  top: 4,
  insetInlineStart: 4,
  zIndex: 1,
  bgcolor: "primary.main",
  color: "primary.contrastText",
  borderRadius: 1,
  px: 0.75,
  lineHeight: 1.6,
  fontSize: 10,
  fontWeight: 700,
};

// "Applies on save" marker for a background removal that's staged on a saved photo.
const pendingBadgeSx = {
  position: "absolute",
  top: 4,
  insetInlineEnd: 4,
  zIndex: 1,
  width: 20,
  height: 20,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  bgcolor: "info.main",
  color: "info.contrastText",
};

// Ring around the photo currently shown in the side preview.
const selectedPhotoTileSx = {
  outline: "3px solid",
  outlineColor: "secondary.main",
  outlineOffset: 2,
};

const variantRowSx = {
  p: 1.25,
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
  transition: "border-color .15s",
  "&:hover": { borderColor: "primary.light" },
};

const variantThumbSx = {
  position: "relative",
  width: 48,
  height: 48,
  borderRadius: 1.5,
  overflow: "hidden",
  flexShrink: 0,
  bgcolor: "background.default",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "1px solid",
  borderColor: "divider",
};

// Also used by the admin Product Approvals page to edit a store's product before publishing it.
// `secondaryAction` ({ label, color, onAfterSave }) adds a second submit button that saves the
// form first and then runs onAfterSave (e.g. approve) before closing.
// `showTranslations` shows the Arabic/Hebrew name + description fields. For now only admins
// translate products (from Product Approvals); store owners don't see or send these fields.
// `reviewer` hides the store-owner review wording without adding a second button - the admin
// Store Products page uses it for products that are already live.
export function ProductFormDialog({
  store,
  product,
  onClose,
  onSaved,
  secondaryAction,
  showTranslations = false,
  reviewer = false,
}) {
  const { t, i18n } = useTranslation(["boProducts", "boShared", "common"]);
  const isEditing = Boolean(product);
  const supportsWholesale = store ? store.type === 2 || store.type === 3 : false;
  // Review wording is for the store owner only - the admin approvals page reuses this dialog
  // (with secondaryAction) and is the reviewer itself.
  const showReviewFlow = !secondaryAction && !reviewer;
  const storeRequiresReview = showReviewFlow && Boolean(store?.requiresProductApproval);
  const isPendingReview = showReviewFlow && isEditing && product.approvalStatus === 2;
  const isRejected = showReviewFlow && isEditing && product.approvalStatus === 3;

  const [currentProductId, setCurrentProductId] = useState(product?.productId ?? null);
  // `images`, `video` and `variants` mirror what the server has. Every media/variant
  // edit made in the dialog is only staged locally (pending*/staged* state below) and
  // sent to the server when the user clicks Save - closing the dialog discards it.
  const [images, setImages] = useState(product?.images ?? []);
  // New photos/video/variants not uploaded yet - uploaded right after the product
  // itself is created or updated.
  const [pendingImages, setPendingImages] = useState([]);
  const [deletedImageIds, setDeletedImageIds] = useState([]);
  // imageId -> { file, previewUrl }: a saved photo swapped for a new file.
  const [imageReplacements, setImageReplacements] = useState({});
  // Saved photos whose background the server should remove on save.
  const [imageBackgroundRemovals, setImageBackgroundRemovals] = useState([]);
  const [mainImageChoice, setMainImageChoice] = useState(null);
  // Photo clicked in the Media grid, shown in the side preview: an imageId for saved photos,
  // the previewUrl for pending ones. Falls back to the main photo when it no longer exists.
  const [selectedPhotoKey, setSelectedPhotoKey] = useState(null);

  const [video, setVideo] = useState(product?.videoUrl ? { videoUrl: product.videoUrl } : null);
  const [pendingVideo, setPendingVideo] = useState(null);
  const [videoRemoved, setVideoRemoved] = useState(false);
  const [videoPlacement, setVideoPlacement] = useState(product?.videoPlacement ?? VIDEO_PLACEMENT_VIDEO_FIRST);

  const [variants, setVariants] = useState(product?.variants ?? []);
  const [pendingVariants, setPendingVariants] = useState([]);
  const [deletedVariantIds, setDeletedVariantIds] = useState([]);
  // variantId -> { payload?, imageFile?, imageUrl?, imagePreviewUrl?, removeImage?, removeBackground? }
  const [variantEdits, setVariantEdits] = useState({});
  const [showVariantForm, setShowVariantForm] = useState(false);
  const [editingVariantId, setEditingVariantId] = useState(null);
  const [editingPendingVariantIndex, setEditingPendingVariantIndex] = useState(null);
  const [variantForm, setVariantForm] = useState(emptyVariantForm);
  const [busyVariantId, setBusyVariantId] = useState(null);
  const [variantImageLinkDialogOpen, setVariantImageLinkDialogOpen] = useState(false);
  const [processingVariantFormBackground, setProcessingVariantFormBackground] = useState(false);

  const [categories, setCategories] = useState([]);
  const [storeCategories, setStoreCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [categoryId, setCategoryId] = useState(product?.categoryId ?? null);

  const [categoryAttributes, setCategoryAttributes] = useState([]);
  const [loadingAttributes, setLoadingAttributes] = useState(false);
  const [attributeValues, setAttributeValues] = useState(() => {
    const map = {};
    (product?.attributeValues || []).forEach((av) => {
      map[av.attributeId] = { optionId: av.optionId ?? null, textValue: av.textValue ?? "" };
    });
    return map;
  });

  const [name, setName] = useState(product?.name ?? "");
  const [nameArabic, setNameArabic] = useState(product?.nameArabic ?? "");
  const [nameHebrew, setNameHebrew] = useState(product?.nameHebrew ?? "");
  const [description, setDescription] = useState(product?.description ?? "");
  const [descriptionArabic, setDescriptionArabic] = useState(product?.descriptionArabic ?? "");
  const [descriptionHebrew, setDescriptionHebrew] = useState(product?.descriptionHebrew ?? "");
  // "name" | "description" | "all" while the Arabic/Hebrew translation is being fetched.
  const [translatingField, setTranslatingField] = useState(null);
  const [draggingPhotos, setDraggingPhotos] = useState(false);
  const fullScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const [price,setPrice] = useState(product ? String(product.price) : "");
  const [wholesalePrice, setWholesalePrice] = useState(product?.wholesalePrice != null ? String(product.wholesalePrice) : "");
  const [discountType, setDiscountType] = useState(product?.discountType ?? DISCOUNT_TYPE_NONE);
  const [discountPrice, setDiscountPrice] = useState(
    // A discount scheduled to start later only has its price in scheduledDiscountPrice.
    (product?.discountPrice ?? product?.scheduledDiscountPrice) != null
      ? String(product.discountPrice ?? product.scheduledDiscountPrice)
      : "",
  );
  const [discountPercent, setDiscountPercent] = useState(product?.discountPercent != null ? String(product.discountPercent) : "");
  const [stockQuantity, setStockQuantity] = useState(product ? String(product.stockQuantity) : "0");
  const [sku, setSku] = useState(product?.sku ?? "");
  const [authenticityOptions, setAuthenticityOptions] = useState([]);
  const [authenticityOptionId, setAuthenticityOptionId] = useState(product?.authenticityOptionId ?? null);
  const [isActive, setIsActive] = useState(product?.isActive ?? true);
  const [requiresSpecialDelivery, setRequiresSpecialDelivery] = useState(product?.requiresSpecialDelivery ?? false);

  const [saving, setSaving] = useState(false);
  const [busyImageId, setBusyImageId] = useState(null);
  const [processingPendingImageIndex, setProcessingPendingImageIndex] = useState(null);
  const [imageLinkDialogOpen, setImageLinkDialogOpen] = useState(false);
  const [formError, setFormError] = useState("");

  // What the dialog shows: the server's media with the staged edits applied.
  const visibleImages = useMemo(() => {
    const list = images
      .filter((img) => !deletedImageIds.includes(img.imageId))
      .map((img) => ({
        ...img,
        imageUrl: imageReplacements[img.imageId]?.previewUrl ?? img.imageUrl,
        isMain: mainImageChoice != null ? img.imageId === mainImageChoice : img.isMain,
        pendingBackgroundRemoval: imageBackgroundRemovals.includes(img.imageId),
      }));
    // The main photo was removed - the server promotes another one, show the first meanwhile.
    if (list.length > 0 && !list.some((img) => img.isMain)) list[0] = { ...list[0], isMain: true };
    return list;
  }, [images, deletedImageIds, imageReplacements, mainImageChoice, imageBackgroundRemovals]);

  const visibleVariants = useMemo(
    () =>
      variants
        .filter((v) => !deletedVariantIds.includes(v.variantId))
        .map((v) => {
          const edit = variantEdits[v.variantId];
          if (!edit) return v;
          return {
            ...v,
            ...edit.payload,
            imageUrl: edit.removeImage ? null : edit.imagePreviewUrl ?? v.imageUrl,
            pendingBackgroundRemoval: Boolean(edit.removeBackground),
          };
        }),
    [variants, deletedVariantIds, variantEdits],
  );

  const visibleVideoUrl = pendingVideo?.previewUrl ?? (!videoRemoved ? video?.videoUrl : null);

  const categoryOptions = useMemo(
    () => buildCategoryOptions(categories, storeCategories, i18n.language),
    [categories, storeCategories, i18n.language]
  );
  const descriptiveAttributes = useMemo(
    () => categoryAttributes.filter((a) => !a.isVariant).sort((a, b) => a.displayOrder - b.displayOrder),
    [categoryAttributes],
  );
  const variantAttributes = useMemo(
    () => categoryAttributes.filter((a) => a.isVariant).sort((a, b) => a.displayOrder - b.displayOrder),
    [categoryAttributes],
  );

  useEffect(() => {
    let active = true;
    getAuthenticityOptions()
      .then((options) => {
        if (active) setAuthenticityOptions(options);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!store) return;
    let active = true;
    setLoadingCategories(true);
    Promise.all([getCategories(), getMyStoreCategories(store.storeId)])
      .then(([allCategories, myStoreCategories]) => {
        if (!active) return;
        setCategories(allCategories.filter((c) => c.isActive));
        setStoreCategories(myStoreCategories);
      })
      .catch(() => {
        if (active) setFormError(t("boProducts:loadCategoriesError"));
      })
      .finally(() => {
        if (active) setLoadingCategories(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);

  useEffect(() => {
    if (categoryOptions.length === 0 || isEditing) return;
    const isValid = categoryOptions.some((c) => c.categoryId === categoryId);
    if (!isValid) setCategoryId(categoryOptions[0].categoryId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryOptions]);

  useEffect(() => {
    if (!categoryId) {
      setCategoryAttributes([]);
      return;
    }
    let active = true;
    setLoadingAttributes(true);
    getCategoryAttributes(categoryId, store?.storeId)
      .then((data) => {
        if (active) setCategoryAttributes(data);
      })
      .catch(() => {
        if (active) setCategoryAttributes([]);
      })
      .finally(() => {
        if (active) setLoadingAttributes(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, store?.storeId]);

  // Adds a store-specific value (e.g. a color the catalog lacks) and merges it into the loaded
  // attribute so it's selectable right away. Throws so the picker can show the error.
  async function addCustomAttributeOption(attributeId, value, colorHex) {
    const option = await createCustomAttributeOption(attributeId, { storeId: store.storeId, value, colorHex });
    setCategoryAttributes((prev) =>
      prev.map((attr) =>
        attr.attributeId !== attributeId || attr.options.some((o) => o.optionId === option.optionId)
          ? attr
          : { ...attr, options: [...attr.options, option] },
      ),
    );
    return option;
  }

  // Lets Ctrl+V paste an image copied from another site (e.g. with the image
  // copier extension) straight into whichever photo area is active - the
  // variant form's photo if it's open, otherwise the main product photos.
  // Only intercepts the paste when clipboard actually holds image data, so
  // normal text pasting into the other fields is untouched.
  useEffect(() => {
    function onPaste(e) {
      const files = imageFilesFromClipboardEvent(e);
      if (files.length === 0) return;
      e.preventDefault();
      setFormError("");
      if (showVariantForm) {
        addVariantImageFile(files[0]);
      } else {
        addPhotoFiles(files);
      }
    }
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  });

  function setAttributeOptionValue(attributeId, optionId) {
    setAttributeValues((prev) => ({ ...prev, [attributeId]: { optionId, textValue: "" } }));
  }

  function setAttributeTextValue(attributeId, textValue) {
    setAttributeValues((prev) => ({ ...prev, [attributeId]: { optionId: null, textValue } }));
  }

  function buildAttributeValuesPayload() {
    return descriptiveAttributes
      .map((attr) => {
        const value = attributeValues[attr.attributeId];
        if (!value) return null;
        if (attr.inputType === ATTRIBUTE_INPUT_DROPDOWN) {
          return value.optionId != null ? { attributeId: attr.attributeId, optionId: value.optionId } : null;
        }
        const trimmed = (value.textValue || "").trim();
        return trimmed ? { attributeId: attr.attributeId, textValue: trimmed } : null;
      })
      .filter(Boolean);
  }

  // Fills the Arabic + Hebrew fields from the English name and/or description (overwriting them).
  // `field` is "name", "description", or "all".
  async function handleTranslate(field) {
    const fields = (field === "all" ? ["name", "description"] : [field]).filter((f) =>
      (f === "name" ? name : description).trim(),
    );
    if (fields.length === 0) return;

    setTranslatingField(field);
    setFormError("");
    try {
      await Promise.all(
        fields.map(async (f) => {
          const { arabic, hebrew } = await translateToArabicAndHebrew(f === "name" ? name : description);
          if (f === "name") {
            setNameArabic(arabic);
            setNameHebrew(hebrew);
          } else {
            setDescriptionArabic(arabic);
            setDescriptionHebrew(hebrew);
          }
        }),
      );
    } catch {
      setFormError(t("boProducts:translateFailed"));
    } finally {
      setTranslatingField(null);
    }
  }

  // Small translate icon shown inside the English name / description inputs.
  function translateAdornment(field, source) {
    if (!showTranslations) return undefined;
    return (
      <InputAdornment position="end" sx={field === "description" ? { alignSelf: "flex-start", mt: 1.5 } : undefined}>
        <Tooltip title={t("boProducts:translateTooltip")}>
          <span>
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleTranslate(field)}
              disabled={!source.trim() || translatingField !== null}
            >
              {translatingField === field || translatingField === "all" ? (
                <CircularProgress size={18} />
              ) : (
                <TranslateRoundedIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </InputAdornment>
    );
  }

  // Ctrl/Cmd + S saves the product from anywhere in the dialog.
  function handleDialogKeyDown(e) {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
      e.preventDefault();
      if (!saving && name.trim() && categoryId) handleSubmit();
    }
  }

  function handlePhotoDrop(e) {
    e.preventDefault();
    setDraggingPhotos(false);
    const files = Array.from(e.dataTransfer?.files || []).filter((f) => f.type.startsWith("image/"));
    if (files.length > 0) addPhotoFiles(files);
  }

  async function handleSubmit(afterSave) {
    // The variant editor only stages its edits on its own "Save Variant" button -
    // if it's still open, block here instead of silently discarding them.
    if (showVariantForm) {
      setFormError(t("boProducts:unsavedVariantError"));
      return;
    }

    const trimmedName = name.trim();
    const parsedPrice = parseFloat(price);

    if (!categoryId) {
      setFormError(t("boProducts:chooseCategoryError"));
      return;
    }
    if (!trimmedName) {
      setFormError(t("boProducts:enterNameError"));
      return;
    }
    if (Number.isNaN(parsedPrice) || parsedPrice < 0) {
      setFormError(t("boProducts:invalidPriceError"));
      return;
    }

    let parsedWholesalePrice = null;
    if (supportsWholesale && wholesalePrice.trim() !== "") {
      parsedWholesalePrice = parseFloat(wholesalePrice);
      if (Number.isNaN(parsedWholesalePrice) || parsedWholesalePrice < 0) {
        setFormError(t("boProducts:invalidWholesalePriceError"));
        return;
      }
    }

    const discountResult = resolveDiscountFields(discountType, discountPrice, discountPercent, parsedPrice, t);
    if (discountResult.error) {
      setFormError(discountResult.error);
      return;
    }

    const attributeValuesPayload = buildAttributeValuesPayload();
    const missingRequired = descriptiveAttributes.filter(
      (a) => a.isRequired && !attributeValuesPayload.some((v) => v.attributeId === a.attributeId),
    );
    if (missingRequired.length > 0) {
      setFormError(t("boProducts:missingAttributes", { list: missingRequired.map((a) => a.attributeName).join(", ") }));
      return;
    }

    setFormError("");
    setSaving(true);

    const payload = {
      categoryId,
      name: trimmedName,
      description: description.trim() || null,
      // Omitted fields keep their stored value on the server; "" clears a translation.
      ...(showTranslations && {
        nameArabic: nameArabic.trim(),
        nameHebrew: nameHebrew.trim(),
        descriptionArabic: descriptionArabic.trim(),
        descriptionHebrew: descriptionHebrew.trim(),
      }),
      price: parsedPrice,
      wholesalePrice: parsedWholesalePrice,
      discountType: discountResult.discountType,
      discountPrice: discountResult.discountPrice,
      discountPercent: discountResult.discountPercent,
      stockQuantity: parseInt(stockQuantity, 10) || 0,
      sku: sku.trim() || null,
      authenticityOptionId,
      requiresSpecialDelivery,
      attributeValues: attributeValuesPayload,
    };

    try {
      let productId = currentProductId;
      let savedMessage;
      if (isEditing) {
        const updated = await updateProduct(productId, { ...payload, isActive, videoPlacement });
        savedMessage =
          isRejected && updated?.approvalStatus === 2
            ? t("boProducts:productResubmitted", { name: trimmedName })
            : t("boProducts:productUpdated", { name: trimmedName });
      } else if (productId) {
        // Product was already created by an earlier Save in this dialog whose media
        // upload partly failed; this submit updates the fields and retries the rest.
        await updateProduct(productId, { ...payload, isActive: true, videoPlacement });
        savedMessage = t("boProducts:productUpdated", { name: trimmedName });
      } else {
        const created = await createProduct({ ...payload, storeId: store.storeId });
        productId = created.productId;
        setCurrentProductId(created.productId);
        setImages(created.images || []);
        savedMessage =
          created.approvalStatus === 2
            ? t("boProducts:productCreatedPendingReview")
            : t("boProducts:productCreatedNotice");
      }

      // Anything that failed stays staged (and the dialog stays open) so Save can retry it.
      const mediaSaved = await applyStagedChanges(productId);
      if (!mediaSaved) return;

      if (isEditing && afterSave) await afterSave();
      onSaved(savedMessage);
    } catch (err) {
      const data = err.response?.data;
      setFormError(typeof data === "string" && data ? data : t("boProducts:saveError"));
    } finally {
      setSaving(false);
    }
  }

  // Sends every staged photo/video/variant change to the server. Each change is cleared
  // from the staged state as soon as it succeeds; returns false if any of them failed.
  async function applyStagedChanges(productId) {
    let allSucceeded = true;
    const fail = (err, fallbackKey) => {
      allSucceeded = false;
      const data = err?.response?.data;
      setFormError(typeof data === "string" && data ? data : t(fallbackKey));
    };

    /* eslint-disable no-await-in-loop */
    // Photos first - the server needs at least one photo before it accepts a video.
    if (pendingImages.length > 0 && !(await uploadPendingImages(productId))) {
      allSucceeded = false;
    }

    for (const [imageIdKey, replacement] of Object.entries(imageReplacements)) {
      const imageId = Number(imageIdKey);
      if (deletedImageIds.includes(imageId)) continue;
      try {
        const updated = await replaceProductImage(productId, imageId, replacement.file);
        setImages(updated.images);
        setImageReplacements(({ [imageIdKey]: _done, ...rest }) => rest);
      } catch (err) {
        fail(err, "boProducts:replacePhotoError");
      }
    }

    for (const imageId of imageBackgroundRemovals) {
      if (deletedImageIds.includes(imageId)) continue;
      try {
        const updated = await removeProductImageBackground(productId, imageId);
        setImages(updated.images);
        setImageBackgroundRemovals((prev) => prev.filter((id) => id !== imageId));
      } catch (err) {
        fail(err, "boProducts:removeBackgroundError");
      }
    }

    for (const imageId of deletedImageIds) {
      try {
        const updated = await deleteProductImage(productId, imageId);
        setImages(updated.images);
        setDeletedImageIds((prev) => prev.filter((id) => id !== imageId));
      } catch (err) {
        fail(err, "boProducts:removePhotoError");
      }
    }

    if (mainImageChoice != null && !deletedImageIds.includes(mainImageChoice)) {
      try {
        const updated = await setMainProductImage(productId, mainImageChoice);
        setImages(updated.images);
        setMainImageChoice(null);
      } catch (err) {
        fail(err, "boProducts:setMainPhotoError");
      }
    }

    if (pendingVideo) {
      try {
        // Uploading replaces any existing video on the server.
        const updated = await uploadProductVideo(productId, pendingVideo.file);
        setVideo(updated.videoUrl ? { videoUrl: updated.videoUrl } : null);
        setPendingVideo(null);
        setVideoRemoved(false);
      } catch (err) {
        fail(err, "boProducts:uploadVideoError");
      }
    } else if (videoRemoved && video) {
      try {
        const updated = await deleteProductVideo(productId);
        setVideo(updated.videoUrl ? { videoUrl: updated.videoUrl } : null);
        setVideoRemoved(false);
      } catch (err) {
        fail(err, "boProducts:removeVideoError");
      }
    }

    for (const variantId of deletedVariantIds) {
      try {
        const updated = await deleteProductVariant(productId, variantId);
        setVariants(updated.variants);
        setDeletedVariantIds((prev) => prev.filter((id) => id !== variantId));
      } catch (err) {
        fail(err, "boProducts:removeVariantError");
      }
    }

    for (const [variantIdKey, edit] of Object.entries(variantEdits)) {
      const variantId = Number(variantIdKey);
      if (deletedVariantIds.includes(variantId)) continue;
      try {
        let updated = null;
        if (edit.payload) {
          updated = await updateProductVariant(productId, variantId, { ...edit.payload, isActive: true });
        }
        if (edit.removeImage) {
          updated = await deleteVariantImage(productId, variantId);
        } else if (edit.imageFile) {
          updated = await uploadVariantImage(productId, variantId, edit.imageFile);
        } else if (edit.imageUrl) {
          updated = await addVariantImageFromUrl(productId, variantId, edit.imageUrl);
        }
        if (edit.removeBackground) {
          updated = await removeVariantImageBackground(productId, variantId);
        }
        if (updated) setVariants(updated.variants);
        setVariantEdits(({ [variantIdKey]: _done, ...rest }) => rest);
      } catch (err) {
        fail(err, "boProducts:saveVariantError");
      }
    }
    /* eslint-enable no-await-in-loop */

    if (pendingVariants.length > 0 && !(await uploadPendingVariants(productId))) {
      allSucceeded = false;
    }

    return allSucceeded;
  }

  function validateImageFile(file) {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setFormError(t("boShared:invalidImageType"));
      return false;
    }
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      setFormError(t("boProducts:imageTooLarge"));
      return false;
    }
    return true;
  }

  function addPhotoFiles(files) {
    const remaining = MAX_IMAGES - (visibleImages.length + pendingImages.length);
    const validFiles = files.slice(0, remaining).filter(validateImageFile);
    if (validFiles.length === 0) return;

    setFormError("");
    setPendingImages((prev) => [
      ...prev,
      ...validFiles.map((file) => ({ file, previewUrl: URL.createObjectURL(file) })),
    ]);
  }

  function handleAddImage(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    addPhotoFiles(files);
  }

  // Reads an image directly from the OS clipboard - lets photos copied with an
  // image-copying browser extension (or any "Copy image" action) be added here
  // with one click, no save-then-upload round trip needed.
  async function handlePasteImageFromClipboard() {
    if (visibleImages.length + pendingImages.length >= MAX_IMAGES) return;
    if (!navigator.clipboard?.read) {
      setFormError(t("boProducts:clipboardPasteUnsupported"));
      return;
    }
    setFormError("");
    try {
      const files = await readImageFilesFromClipboard();
      if (files.length === 0) {
        setFormError(t("boProducts:noImageInClipboard"));
        return;
      }
      addPhotoFiles(files);
    } catch {
      setFormError(t("boProducts:clipboardPasteError"));
    }
  }

  function removePendingImage(index) {
    setPendingImages((prev) => prev.filter((_, i) => i !== index));
  }

  // Returns false if any photo failed; the failed ones stay pending for a retry.
  async function uploadPendingImages(targetProductId) {
    const failed = [];
    for (const pending of pendingImages) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const updated = pending.file
          ? await uploadProductImage(targetProductId, pending.file)
          : await addProductImageFromUrl(targetProductId, pending.url);
        setImages(updated.images);
      } catch {
        failed.push(pending);
        setFormError(t("boProducts:someImagesFailedUpload"));
      }
    }
    setPendingImages(failed);
    return failed.length === 0;
  }

  function handleAddImageFromUrl(url) {
    if (!url) return;
    const remaining = MAX_IMAGES - (visibleImages.length + pendingImages.length);
    if (remaining <= 0) return;
    setPendingImages((prev) => [...prev, { url, previewUrl: url }]);
    setImageLinkDialogOpen(false);
  }

  async function handleRemoveImageBackground(imageId) {
    setFormError("");
    const replacement = imageReplacements[imageId];
    // A not-yet-uploaded replacement can be processed right away; a photo that's already
    // on the server is processed there when the product is saved.
    if (!replacement) {
      setImageBackgroundRemovals((prev) => (prev.includes(imageId) ? prev : [...prev, imageId]));
      return;
    }
    setBusyImageId(imageId);
    try {
      const blob = await removeImageBackground(replacement.file);
      const resultFile = new File([blob], `bg-removed-${Date.now()}.png`, { type: "image/png" });
      setImageReplacements((prev) => ({
        ...prev,
        [imageId]: { file: resultFile, previewUrl: URL.createObjectURL(resultFile) },
      }));
    } catch {
      setFormError(t("boProducts:removeBackgroundError"));
    } finally {
      setBusyImageId(null);
    }
  }

  async function handleRemovePendingImageBackground(index) {
    const pending = pendingImages[index];
    if (!pending?.file) return;

    setFormError("");
    setProcessingPendingImageIndex(index);
    try {
      const blob = await removeImageBackground(pending.file);
      const resultFile = new File([blob], `bg-removed-${Date.now()}.png`, { type: "image/png" });
      setPendingImages((prev) =>
        prev.map((p, i) => (i === index ? { file: resultFile, previewUrl: URL.createObjectURL(resultFile) } : p)),
      );
    } catch {
      setFormError(t("boProducts:removeBackgroundError"));
    } finally {
      setProcessingPendingImageIndex(null);
    }
  }

  function handleReplaceImage(imageId, e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !validateImageFile(file)) return;

    setFormError("");
    setImageReplacements((prev) => ({ ...prev, [imageId]: { file, previewUrl: URL.createObjectURL(file) } }));
    // The new file replaces the old one, so a background removal staged for it no longer applies.
    setImageBackgroundRemovals((prev) => prev.filter((id) => id !== imageId));
  }

  function handleDeleteImage(imageId) {
    setFormError("");
    setDeletedImageIds((prev) => [...prev, imageId]);
    if (mainImageChoice === imageId) setMainImageChoice(null);
  }

  function handleSetMainImage(imageId) {
    setFormError("");
    setMainImageChoice(imageId);
  }

  function handleAddVideo(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
      setFormError(t("boProducts:invalidVideoType"));
      return;
    }
    if (file.size > MAX_VIDEO_SIZE_BYTES) {
      setFormError(t("boProducts:videoTooLarge"));
      return;
    }

    setFormError("");
    setPendingVideo({ file, previewUrl: URL.createObjectURL(file) });
  }

  function handleDeleteVideo() {
    setFormError("");
    if (pendingVideo) {
      setPendingVideo(null);
    }
    if (video) setVideoRemoved(true);
  }

  function openAddVariant() {
    setEditingVariantId(null);
    setEditingPendingVariantIndex(null);
    setVariantForm(emptyVariantForm);
    setShowVariantForm(true);
  }

  // `variant` is the displayed one, i.e. with any staged edits already applied.
  function openEditVariant(variant) {
    const edit = variantEdits[variant.variantId];
    setEditingVariantId(variant.variantId);
    setEditingPendingVariantIndex(null);
    const attributeValuesMap = {};
    (variant.attributeValues || []).forEach((av) => {
      attributeValuesMap[av.attributeId] = av.optionId;
    });
    setVariantForm({
      variantName: variant.variantName || "",
      price: String(variant.price),
      stockQuantity: String(variant.stockQuantity),
      sku: variant.sku || "",
      discountType: variant.discountType ?? DISCOUNT_TYPE_NONE,
      discountPrice:
        (variant.discountPrice ?? variant.scheduledDiscountPrice) != null
          ? String(variant.discountPrice ?? variant.scheduledDiscountPrice)
          : "",
      discountPercent: variant.discountPercent != null ? String(variant.discountPercent) : "",
      attributeValues: attributeValuesMap,
      imageFile: edit?.imageFile ?? null,
      imageUrl: edit?.imageUrl ?? null,
      imagePreviewUrl: variant.imageUrl || null,
      removeBackground: Boolean(edit?.removeBackground),
    });
    setShowVariantForm(true);
  }

  // A variant staged locally that doesn't exist on the server yet - created via
  // uploadPendingVariants() on save (same pattern as pendingImages/pendingVideo).
  function openEditPendingVariant(index) {
    const pending = pendingVariants[index];
    setEditingVariantId(null);
    setEditingPendingVariantIndex(index);
    const attributeValuesMap = {};
    (pending.attributeValues || []).forEach((av) => {
      attributeValuesMap[av.attributeId] = av.optionId;
    });
    setVariantForm({
      variantName: pending.variantName || "",
      price: String(pending.price),
      stockQuantity: String(pending.stockQuantity),
      sku: pending.sku || "",
      discountType: pending.discountType ?? DISCOUNT_TYPE_NONE,
      discountPrice: pending.discountPrice != null ? String(pending.discountPrice) : "",
      discountPercent: pending.discountPercent != null ? String(pending.discountPercent) : "",
      attributeValues: attributeValuesMap,
      imageFile: pending.imageFile,
      imageUrl: pending.imageUrl,
      imagePreviewUrl: pending.imagePreviewUrl,
    });
    setShowVariantForm(true);
  }

  function removePendingVariant(index) {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t("boProducts:removeVariantConfirm"))) return;
    setPendingVariants((prev) => prev.filter((_, i) => i !== index));
  }

  function cancelVariantForm() {
    setShowVariantForm(false);
    setEditingVariantId(null);
    setEditingPendingVariantIndex(null);
    setVariantForm(emptyVariantForm);
  }

  function setVariantAttributeOption(attributeId, optionId) {
    setVariantForm((prev) => ({ ...prev, attributeValues: { ...prev.attributeValues, [attributeId]: optionId } }));
  }

  function addVariantImageFile(file) {
    if (!file || !validateImageFile(file)) return;
    setVariantForm((prev) => ({
      ...prev,
      imageFile: file,
      imageUrl: null,
      imagePreviewUrl: URL.createObjectURL(file),
      removeBackground: false,
    }));
  }

  function handleVariantImageFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    addVariantImageFile(file);
  }

  // Same idea as handlePasteImageFromClipboard, but for the variant photo picker.
  async function handlePasteVariantImageFromClipboard() {
    if (!navigator.clipboard?.read) {
      setFormError(t("boProducts:clipboardPasteUnsupported"));
      return;
    }
    setFormError("");
    try {
      const files = await readImageFilesFromClipboard();
      if (files.length === 0) {
        setFormError(t("boProducts:noImageInClipboard"));
        return;
      }
      addVariantImageFile(files[0]);
    } catch {
      setFormError(t("boProducts:clipboardPasteError"));
    }
  }

  function handleVariantImageFromUrl(url) {
    if (!url) return;
    setVariantForm((prev) => ({ ...prev, imageFile: null, imageUrl: url, imagePreviewUrl: url, removeBackground: false }));
    setVariantImageLinkDialogOpen(false);
  }

  // Background removal on a saved variant's row: a staged (not yet uploaded) file is
  // processed right away, a photo already on the server is processed there on save.
  async function handleRemoveVariantImageBackground(variantId) {
    setFormError("");
    const edit = variantEdits[variantId];
    if (!edit?.imageFile) {
      setVariantEdits((prev) => ({ ...prev, [variantId]: { ...prev[variantId], removeBackground: true } }));
      return;
    }
    setBusyVariantId(variantId);
    try {
      const blob = await removeImageBackground(edit.imageFile);
      const resultFile = new File([blob], `bg-removed-${Date.now()}.png`, { type: "image/png" });
      setVariantEdits((prev) => ({
        ...prev,
        [variantId]: { ...prev[variantId], imageFile: resultFile, imagePreviewUrl: URL.createObjectURL(resultFile) },
      }));
    } catch {
      setFormError(t("boProducts:removeBackgroundError"));
    } finally {
      setBusyVariantId(null);
    }
  }

  async function handleRemoveVariantFormImageBackground() {
    setFormError("");
    if (variantForm.imageFile) {
      setProcessingVariantFormBackground(true);
      try {
        const blob = await removeImageBackground(variantForm.imageFile);
        const resultFile = new File([blob], `bg-removed-${Date.now()}.png`, { type: "image/png" });
        setVariantForm((prev) => ({ ...prev, imageFile: resultFile, imageUrl: null, imagePreviewUrl: URL.createObjectURL(resultFile) }));
      } catch {
        setFormError(t("boProducts:removeBackgroundError"));
      } finally {
        setProcessingVariantFormBackground(false);
      }
      return;
    }
    if (editingVariantId) {
      setVariantForm((prev) => ({ ...prev, removeBackground: true }));
    }
  }

  function handleDeleteVariantImage(variantId) {
    setFormError("");
    const hasSavedImage = Boolean(variants.find((v) => v.variantId === variantId)?.imageUrl);
    setVariantEdits((prev) => {
      const {
        imageFile: _file,
        imageUrl: _url,
        imagePreviewUrl: _preview,
        removeBackground: _bg,
        ...rest
      } = prev[variantId] || {};
      return { ...prev, [variantId]: { ...rest, removeImage: hasSavedImage } };
    });
  }

  function saveVariant() {
    const trimmedName = variantForm.variantName.trim();
    const parsedPrice = parseFloat(variantForm.price);
    const variantAttributeValuesPayload = buildVariantAttributeValuesPayload(
      variantAttributes,
      variantForm.attributeValues,
    );

    if (variantAttributes.length > 0 && variantAttributeValuesPayload.length !== variantAttributes.length) {
      setFormError(
        t("boProducts:missingVariantAttributes", {
          attributes: variantAttributes.map((a) => a.attributeName).join(", "),
        }),
      );
      return;
    }
    if (!trimmedName && variantAttributeValuesPayload.length === 0) {
      setFormError(t("boProducts:missingVariantName"));
      return;
    }
    if (Number.isNaN(parsedPrice) || parsedPrice < 0) {
      setFormError(t("boProducts:invalidVariantPrice"));
      return;
    }

    const discountResult = resolveDiscountFields(
      variantForm.discountType,
      variantForm.discountPrice,
      variantForm.discountPercent,
      parsedPrice,
      t,
    );
    if (discountResult.error) {
      setFormError(discountResult.error);
      return;
    }

    setFormError("");

    const payload = {
      variantName: trimmedName || null,
      price: parsedPrice,
      discountType: discountResult.discountType,
      discountPrice: discountResult.discountPrice,
      discountPercent: discountResult.discountPercent,
      stockQuantity: parseInt(variantForm.stockQuantity, 10) || 0,
      sku: variantForm.sku.trim() || null,
      attributeValues: variantAttributeValuesPayload,
    };

    if (editingVariantId) {
      const hasNewImage = Boolean(variantForm.imageFile || variantForm.imageUrl);
      setVariantEdits((prev) => ({
        ...prev,
        [editingVariantId]: {
          ...prev[editingVariantId],
          payload,
          ...(hasNewImage && {
            imageFile: variantForm.imageFile,
            imageUrl: variantForm.imageUrl,
            imagePreviewUrl: variantForm.imagePreviewUrl,
            removeImage: false,
          }),
          removeBackground: Boolean(variantForm.removeBackground),
        },
      }));
      cancelVariantForm();
      return;
    }

    setPendingVariants((prev) => {
      const entry = {
        ...payload,
        imageFile: variantForm.imageFile,
        imageUrl: variantForm.imageUrl,
        imagePreviewUrl: variantForm.imagePreviewUrl,
      };
      if (editingPendingVariantIndex != null) {
        const copy = [...prev];
        copy[editingPendingVariantIndex] = entry;
        return copy;
      }
      return [...prev, entry];
    });
    cancelVariantForm();
  }

  // Returns false if any variant failed; the failed ones stay pending for a retry.
  async function uploadPendingVariants(targetProductId) {
    const failed = [];
    let currentVariants = null;
    for (const pending of pendingVariants) {
      try {
        const previousIds = new Set((currentVariants ?? variants).map((v) => v.variantId));
        const { imageFile, imageUrl, imagePreviewUrl, ...variantPayload } = pending;
        // eslint-disable-next-line no-await-in-loop
        const updated = await createProductVariant(targetProductId, variantPayload);
        currentVariants = updated.variants;
        setVariants(currentVariants);

        if (imageFile || imageUrl) {
          const createdVariant = currentVariants.find((v) => !previousIds.has(v.variantId));
          if (createdVariant) {
            try {
              // eslint-disable-next-line no-await-in-loop
              const withImage = imageFile
                ? await uploadVariantImage(targetProductId, createdVariant.variantId, imageFile)
                : await addVariantImageFromUrl(targetProductId, createdVariant.variantId, imageUrl);
              currentVariants = withImage.variants;
              setVariants(currentVariants);
            } catch {
              setFormError(t("boProducts:uploadVariantPhotoError"));
            }
          }
        }
      } catch {
        failed.push(pending);
        setFormError(t("boProducts:saveVariantError"));
      }
    }
    setPendingVariants(failed);
    return failed.length === 0;
  }

  function confirmDeleteVariant(variantId) {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t("boProducts:removeVariantConfirm"))) return;
    setFormError("");
    setDeletedVariantIds((prev) => [...prev, variantId]);
  }

  const photoCount = visibleImages.length + pendingImages.length;
  const canAddPhotos = photoCount < MAX_IMAGES;
  const mainImageUrl = (visibleImages.find((img) => img.isMain) ?? visibleImages[0])?.imageUrl ?? pendingImages[0]?.previewUrl;
  const selectedPhotoUrl =
    visibleImages.find((img) => img.imageId === selectedPhotoKey)?.imageUrl ??
    pendingImages.find((p) => p.previewUrl === selectedPhotoKey)?.previewUrl;
  const previewImageUrl = selectedPhotoUrl ?? mainImageUrl;

  // Live price preview - mirrors the rules in resolveDiscountFields, but never errors.
  const previewPrice = parseFloat(price);
  const hasValidPrice = !Number.isNaN(previewPrice) && previewPrice >= 0;
  let previewSalePrice = null;
  if (hasValidPrice && discountType === DISCOUNT_TYPE_FIXED) {
    const value = parseFloat(discountPrice);
    if (value > 0 && value < previewPrice) previewSalePrice = value;
  } else if (hasValidPrice && discountType === DISCOUNT_TYPE_PERCENTAGE) {
    const percent = parseFloat(discountPercent);
    if (percent > 0 && percent < 100) previewSalePrice = Math.round(previewPrice * (100 - percent)) / 100;
  }
  const previewDiscountPercent =
    previewSalePrice != null && previewPrice > 0 ? Math.round((1 - previewSalePrice / previewPrice) * 100) : null;
  const isOutOfStock = stockQuantity !== "" && Number(stockQuantity) <= 0;

  // Description translations only count once there's an English description to translate.
  const translationFields = description.trim()
    ? [nameArabic, nameHebrew, descriptionArabic, descriptionHebrew]
    : [nameArabic, nameHebrew];
  const translatedCount = translationFields.filter((v) => v.trim()).length;
  const translationLanguages = [
    {
      code: "ar",
      nativeName: "العربية",
      label: t("boProducts:languageArabic"),
      name: nameArabic,
      setName: setNameArabic,
      nameLabel: t("boProducts:nameArabic"),
      description: descriptionArabic,
      setDescription: setDescriptionArabic,
      descriptionLabel: t("boProducts:descriptionArabic"),
    },
    {
      code: "he",
      nativeName: "עברית",
      label: t("boProducts:languageHebrew"),
      name: nameHebrew,
      setName: setNameHebrew,
      nameLabel: t("boProducts:nameHebrew"),
      description: descriptionHebrew,
      setDescription: setDescriptionHebrew,
      descriptionLabel: t("boProducts:descriptionHebrew"),
    },
  ];

  const isNewUnsaved = !isEditing && !currentProductId;
  const canSubmit = !saving && Boolean(name.trim()) && Boolean(categoryId);
  let submitLabel;
  if (saving) submitLabel = t("boShared:saving");
  else if (!isNewUnsaved) submitLabel = isRejected ? t("boProducts:saveAndResubmit") : t("boProducts:saveChanges");
  else submitLabel = storeRequiresReview ? t("boProducts:sendForReview") : t("boProducts:createProduct");

  // Inputs sitting on a tinted panel keep a solid background so they read as fields.
  const fieldOnTintSx = { "& .MuiOutlinedInput-root": { bgcolor: "background.paper" } };
  const discountToggleSx = { "& .MuiToggleButton-root": { px: 1.75, textTransform: "none", fontWeight: 600 } };

  return (
    <Dialog
      open
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="lg"
      fullScreen={fullScreen}
      scroll="paper"
      onKeyDown={handleDialogKeyDown}
      slotProps={{
        paper: {
          sx: {
            borderRadius: fullScreen ? 0 : 3,
            height: fullScreen ? "100%" : "min(92vh, 1100px)",
            bgcolor: "background.default",
            backgroundImage: "none",
          },
        },
      }}
    >
      {/* ---------- Header ---------- */}
      <Stack
        direction="row"
        spacing={1.75}
        alignItems="center"
        sx={{ px: { xs: 2, sm: 3 }, py: 1.75, bgcolor: "background.paper", borderBottom: "1px solid", borderColor: "divider" }}
      >
        <Avatar
          variant="rounded"
          src={mainImageUrl}
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            bgcolor: "action.hover",
            color: "text.secondary",
            border: "1px solid",
            borderColor: "divider",
            "& img": { objectFit: "contain" },
          }}
        >
          <Inventory2RoundedIcon />
        </Avatar>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography variant="h6" noWrap sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              {isEditing ? name.trim() || product.name : t("boProducts:newProductTitle")}
            </Typography>
            {isEditing && (
              <Stack direction="row" spacing={0.75} sx={{ display: { xs: "none", sm: "flex" }, flexShrink: 0 }}>
                <Chip
                  size="small"
                  label={isActive ? t("boShared:active") : t("boShared:inactive")}
                  color={isActive ? "success" : "default"}
                  variant={isActive ? "filled" : "outlined"}
                />
                {product.approvalStatus === 2 && <Chip size="small" color="warning" label={t("boProducts:underReview")} />}
                {product.approvalStatus === 3 && <Chip size="small" color="error" label={t("boProducts:rejected")} />}
              </Stack>
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" noWrap>
            {[isEditing ? t("boProducts:editProduct") : t("boProducts:newProductSubtitle"), store?.name].filter(Boolean).join(" · ")}
          </Typography>
        </Box>
        <Tooltip title={t("common:close")}>
          <span>
            <IconButton onClick={onClose} disabled={saving}>
              <CloseRoundedIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Grid container spacing={{ xs: 2, md: 3 }}>
          {(isPendingReview || isRejected) && (
            <Grid size={12}>
              {isPendingReview && (
                <Alert severity="warning" icon={<HourglassTopRoundedIcon fontSize="inherit" />} sx={{ borderRadius: 2 }}>
                  <Typography variant="subtitle2">{t("boProducts:pendingReviewTitle")}</Typography>
                  {t("boProducts:pendingReviewText")}
                </Alert>
              )}
              {isRejected && (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  <Typography variant="subtitle2">{t("boProducts:rejectedTitle")}</Typography>
                  {product.approvalRejectionReason && (
                    <Box>{t("boProducts:rejectedReason", { reason: product.approvalRejectionReason })}</Box>
                  )}
                  <Box>{t("boProducts:rejectedHint")}</Box>
                </Alert>
              )}
            </Grid>
          )}

          {/* ---------- Main column ---------- */}
          <Grid size={{ xs: 12, md: 8 }} sx={{ order: { xs: 2, md: 1 } }}>
            <Stack spacing={{ xs: 2, md: 3 }}>
              {/* Basic information */}
              <FormSection
                icon={ArticleOutlinedIcon}
                title={t("boProducts:sectionBasicInfo")}
                subtitle={t("boProducts:sectionBasicInfoHint")}
              >
                <Stack spacing={2}>
                  <TextField
                    label={t("boProducts:name")}
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    fullWidth
                    autoFocus={!isEditing}
                    slotProps={{ input: { endAdornment: translateAdornment("name", name) } }}
                  />
                  <TextField
                    label={t("boShared:description")}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    fullWidth
                    multiline
                    minRows={4}
                    maxRows={14}
                    slotProps={{ input: { endAdornment: translateAdornment("description", description) } }}
                  />
                </Stack>
              </FormSection>

              {/* Translations */}
              {showTranslations && (
                <FormSection
                  icon={TranslateRoundedIcon}
                  title={t("boProducts:sectionTranslations")}
                  subtitle={t("boProducts:sectionTranslationsHint")}
                  action={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        size="small"
                        label={t("boProducts:translatedCount", { done: translatedCount, total: translationFields.length })}
                        color={translatedCount === translationFields.length ? "success" : "default"}
                        variant={translatedCount === translationFields.length ? "filled" : "outlined"}
                        sx={{ display: { xs: "none", sm: "flex" } }}
                      />
                      <Button
                        size="small"
                        variant="contained"
                        disableElevation
                        onClick={() => handleTranslate("all")}
                        disabled={translatingField !== null || (!name.trim() && !description.trim())}
                        startIcon={
                          translatingField === "all" ? <CircularProgress size={14} color="inherit" /> : <TranslateRoundedIcon />
                        }
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        {t("boProducts:translateAll")}
                      </Button>
                    </Stack>
                  }
                >
                  <Grid container spacing={2}>
                    {translationLanguages.map((lang) => {
                      const complete = Boolean(lang.name.trim()) && (!description.trim() || Boolean(lang.description.trim()));
                      return (
                        <Grid key={lang.code} size={{ xs: 12, sm: 6 }}>
                          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "action.hover", height: "100%" }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                {lang.nativeName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {lang.label}
                              </Typography>
                              <Box sx={{ flexGrow: 1 }} />
                              {complete && <CheckCircleRoundedIcon color="success" sx={{ fontSize: 18 }} />}
                            </Stack>
                            <Stack spacing={1.5}>
                              <TextField
                                label={lang.nameLabel}
                                value={lang.name}
                                onChange={(e) => lang.setName(e.target.value)}
                                size="small"
                                fullWidth
                                slotProps={{ htmlInput: { dir: "rtl", lang: lang.code } }}
                                sx={fieldOnTintSx}
                              />
                              <TextField
                                label={lang.descriptionLabel}
                                value={lang.description}
                                onChange={(e) => lang.setDescription(e.target.value)}
                                size="small"
                                fullWidth
                                multiline
                                minRows={3}
                                maxRows={10}
                                slotProps={{ htmlInput: { dir: "rtl", lang: lang.code } }}
                                sx={fieldOnTintSx}
                              />
                            </Stack>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </FormSection>
              )}

              {/* Media */}
              <FormSection
                icon={PhotoLibraryOutlinedIcon}
                title={t("boProducts:sectionMedia")}
                subtitle={t("boProducts:sectionMediaHint")}
                action={
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${photoCount} / ${MAX_IMAGES}`}
                    color={canAddPhotos ? "default" : "warning"}
                  />
                }
              >
                <Box
                  onDragOver={(e) => {
                    if (!canAddPhotos) return;
                    e.preventDefault();
                    setDraggingPhotos(true);
                  }}
                  onDragLeave={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget)) setDraggingPhotos(false);
                  }}
                  onDrop={canAddPhotos ? handlePhotoDrop : undefined}
                  sx={{
                    p: 1,
                    m: -1,
                    borderRadius: 2.5,
                    border: "2px dashed",
                    borderColor: draggingPhotos ? "primary.main" : "transparent",
                    bgcolor: draggingPhotos ? "action.hover" : "transparent",
                    transition: "all .15s",
                  }}
                >
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                    {visibleImages.map((img) => (
                        <Box
                          key={img.imageId}
                          sx={{
                            ...mediaTileSx,
                            ...(img.isMain && { border: "2px solid", borderColor: "primary.main" }),
                            ...(img.imageId === selectedPhotoKey && selectedPhotoTileSx),
                          }}
                        >
                          <Tooltip title={t("boProducts:previewPhoto")}>
                            <Box
                              component="img"
                              src={img.imageUrl}
                              alt=""
                              onClick={() => setSelectedPhotoKey(img.imageId)}
                              sx={{ width: "100%", height: "100%", objectFit: "contain", cursor: "zoom-in" }}
                            />
                          </Tooltip>
                          {img.isMain && <Box sx={mainPhotoBadgeSx}>{t("boProducts:mainPhoto")}</Box>}
                          {img.pendingBackgroundRemoval && (
                            <Tooltip title={t("boProducts:backgroundRemovalOnSave")}>
                              <Box sx={pendingBadgeSx}>
                                <AutoFixHighRoundedIcon sx={{ fontSize: 12 }} />
                              </Box>
                            </Tooltip>
                          )}
                          {busyImageId === img.imageId && (
                            <Box sx={mediaTileBusySx}>
                              <CircularProgress size={20} sx={{ color: "#fff" }} />
                            </Box>
                          )}
                          <Box sx={mediaTileActionsSx}>
                            <Tooltip title={t("boProducts:replacePhoto")}>
                              <IconButton size="small" component="label">
                                <SwapHorizRoundedIcon sx={{ fontSize: 16 }} />
                                <input type="file" accept="image/*" hidden onChange={(e) => handleReplaceImage(img.imageId, e)} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={img.isMain ? t("boProducts:mainPhoto") : t("boProducts:setAsMain")}>
                              <IconButton size="small" onClick={() => !img.isMain && handleSetMainImage(img.imageId)}>
                                {img.isMain ? (
                                  <StarRoundedIcon sx={{ fontSize: 16, color: "#FFC857" }} />
                                ) : (
                                  <StarOutlineRoundedIcon sx={{ fontSize: 16 }} />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t("boProducts:removeBackground")}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleRemoveImageBackground(img.imageId);
                                }}
                              >
                                <AutoFixHighRoundedIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t("common:delete")}>
                              <IconButton size="small" onClick={() => handleDeleteImage(img.imageId)}>
                                <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      ))}

                    {pendingImages.map((pending, index) => (
                        <Box
                          key={index}
                          sx={{
                            ...mediaTileSx,
                            ...(visibleImages.length === 0 && index === 0 && { border: "2px solid", borderColor: "primary.main" }),
                            ...(pending.previewUrl === selectedPhotoKey && selectedPhotoTileSx),
                          }}
                        >
                          <Tooltip title={t("boProducts:previewPhoto")}>
                            <Box
                              component="img"
                              src={pending.previewUrl}
                              alt=""
                              onClick={() => setSelectedPhotoKey(pending.previewUrl)}
                              sx={{ width: "100%", height: "100%", objectFit: "contain", cursor: "zoom-in" }}
                            />
                          </Tooltip>
                          {visibleImages.length === 0 && index === 0 && <Box sx={mainPhotoBadgeSx}>{t("boProducts:mainPhoto")}</Box>}
                          {processingPendingImageIndex === index && (
                            <Box sx={mediaTileBusySx}>
                              <CircularProgress size={20} sx={{ color: "#fff" }} />
                            </Box>
                          )}
                          <Box sx={mediaTileActionsSx}>
                            {pending.file && (
                              <Tooltip title={t("boProducts:removeBackground")}>
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRemovePendingImageBackground(index)}
                                    disabled={processingPendingImageIndex === index}
                                  >
                                    <AutoFixHighRoundedIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            <Tooltip title={t("common:delete")}>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => removePendingImage(index)}
                                  disabled={processingPendingImageIndex === index}
                                >
                                  <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </Box>
                      ))}

                    {canAddPhotos && (
                      <Box
                        component="label"
                        sx={{
                          ...mediaTileSx,
                          border: "1.5px dashed",
                          borderColor: "divider",
                          bgcolor: "transparent",
                          flexDirection: "column",
                          gap: 0.25,
                          px: 1,
                          textAlign: "center",
                          color: "text.secondary",
                          cursor: "pointer",
                          transition: "all .15s",
                          "&:hover": { borderColor: "primary.main", color: "primary.main", bgcolor: "action.hover" },
                        }}
                      >
                        <CloudUploadOutlinedIcon sx={{ fontSize: 24 }} />
                        <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                          {t("boProducts:uploadPhotos")}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: 10, lineHeight: 1.2, color: "text.disabled" }}>
                          {t("boProducts:orDragAndDrop")}
                        </Typography>
                        <input type="file" accept="image/*" multiple hidden onChange={handleAddImage} />
                      </Box>
                    )}
                  </Box>
                </Box>

                {canAddPhotos && (
                  <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap", rowGap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      startIcon={<InsertLinkRoundedIcon />}
                      onClick={() => setImageLinkDialogOpen(true)}
                      sx={{ borderColor: "divider" }}
                    >
                      {t("boProducts:addFromLink")}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      startIcon={<ContentPasteGoRoundedIcon />}
                      onClick={handlePasteImageFromClipboard}
                      sx={{ borderColor: "divider" }}
                    >
                      {t("boProducts:pasteImage")}
                    </Button>
                  </Stack>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  {t("boProducts:pasteImageHint")}
                </Typography>

                <Divider sx={{ my: 2.5 }} />

                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <VideocamOutlinedIcon fontSize="small" color="action" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {t("boProducts:video")}
                  </Typography>
                </Stack>
                {photoCount === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 1.25, borderRadius: 2, bgcolor: "action.hover" }}>
                    {t("boProducts:addPhotoBeforeVideoHint")}
                  </Typography>
                ) : (
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "flex-start" }}>
                    {visibleVideoUrl ? (
                      <Box sx={{ ...mediaTileSx, width: 220, height: 140, bgcolor: "#000" }}>
                        <Box
                          component="video"
                          src={visibleVideoUrl}
                          controls
                          sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                        />
                        <Tooltip title={t("common:delete")}>
                          <IconButton
                            size="small"
                            onClick={handleDeleteVideo}
                            sx={{
                              position: "absolute",
                              top: 4,
                              insetInlineEnd: 4,
                              bgcolor: "rgba(0,0,0,0.55)",
                              color: "#fff",
                              p: 0.5,
                              "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
                            }}
                          >
                            <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Box
                        component="label"
                        sx={{
                          ...mediaTileSx,
                          width: 220,
                          border: "1.5px dashed",
                          borderColor: "divider",
                          bgcolor: "transparent",
                          flexDirection: "column",
                          gap: 0.5,
                          color: "text.secondary",
                          cursor: "pointer",
                          transition: "all .15s",
                          "&:hover": { borderColor: "primary.main", color: "primary.main", bgcolor: "action.hover" },
                        }}
                      >
                        <VideocamOutlinedIcon sx={{ fontSize: 24 }} />
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {t("boProducts:addVideo")}
                        </Typography>
                        <input type="file" accept="video/mp4,video/quicktime" hidden onChange={handleAddVideo} />
                      </Box>
                    )}

                    {isEditing && visibleVideoUrl && (
                      <TextField
                        select
                        size="small"
                        label={t("boProducts:videoPlacement")}
                        value={videoPlacement}
                        onChange={(e) => setVideoPlacement(Number(e.target.value))}
                        sx={{ minWidth: 260, flexGrow: 1 }}
                      >
                        {VIDEO_PLACEMENT_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {t(option.labelKey)}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </Stack>
                )}
              </FormSection>

              {/* Pricing & inventory */}
              <FormSection icon={SellOutlinedIcon} title={t("boProducts:sectionPricing")} subtitle={t("boProducts:sectionPricingHint")}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label={t("common:price")}
                      required
                      type="number"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      fullWidth
                      slotProps={{
                        input: { startAdornment: <InputAdornment position="start">₪</InputAdornment> },
                        htmlInput: { min: 0, step: "0.01" },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label={t("boProducts:stockQuantity")}
                      type="number"
                      value={stockQuantity}
                      onChange={(e) => setStockQuantity(e.target.value)}
                      fullWidth
                      helperText={isOutOfStock ? t("boProducts:outOfStock") : undefined}
                      slotProps={{ htmlInput: { min: 0, step: 1 } }}
                      sx={isOutOfStock ? { "& .MuiFormHelperText-root": { color: "warning.main", fontWeight: 600 } } : undefined}
                    />
                  </Grid>
                  {supportsWholesale && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label={t("boProducts:wholesalePriceOptional")}
                        type="number"
                        value={wholesalePrice}
                        onChange={(e) => setWholesalePrice(e.target.value)}
                        fullWidth
                        slotProps={{
                          input: { startAdornment: <InputAdornment position="start">₪</InputAdornment> },
                          htmlInput: { min: 0, step: "0.01" },
                        }}
                      />
                    </Grid>
                  )}
                  <Grid size={{ xs: 12, sm: supportsWholesale ? 6 : 12 }}>
                    <TextField label={t("boProducts:skuOptional")} value={sku} onChange={(e) => setSku(e.target.value)} fullWidth />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2.5 }} />

                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
                  {t("boShared:discount")}
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }}>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    color="primary"
                    value={discountType}
                    onChange={(_, value) => value != null && setDiscountType(value)}
                    sx={discountToggleSx}
                  >
                    <ToggleButton value={DISCOUNT_TYPE_NONE}>{t("boProducts:discountNone")}</ToggleButton>
                    <ToggleButton value={DISCOUNT_TYPE_FIXED}>{t("boProducts:discountFixed")}</ToggleButton>
                    <ToggleButton value={DISCOUNT_TYPE_PERCENTAGE}>{t("boProducts:discountPercentage")}</ToggleButton>
                  </ToggleButtonGroup>
                  {discountType === DISCOUNT_TYPE_FIXED && (
                    <TextField
                      label={t("boProducts:salePrice")}
                      type="number"
                      value={discountPrice}
                      onChange={(e) => setDiscountPrice(e.target.value)}
                      size="small"
                      sx={{ flexGrow: 1 }}
                      slotProps={{
                        input: { startAdornment: <InputAdornment position="start">₪</InputAdornment> },
                        htmlInput: { min: 0, step: "0.01" },
                      }}
                    />
                  )}
                  {discountType === DISCOUNT_TYPE_PERCENTAGE && (
                    <TextField
                      label={t("boProducts:discountPercentLabel")}
                      type="number"
                      value={discountPercent}
                      onChange={(e) => setDiscountPercent(e.target.value)}
                      size="small"
                      sx={{ flexGrow: 1 }}
                      slotProps={{
                        input: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
                        htmlInput: { min: 0, max: 100, step: 1 },
                      }}
                    />
                  )}
                </Stack>
                {previewSalePrice != null && (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                      mt: 1.5,
                      px: 1.5,
                      py: 1,
                      borderRadius: 2,
                      bgcolor: (theme) => alpha(theme.palette.success.main, 0.08),
                      color: "success.dark",
                    }}
                  >
                    <SellOutlinedIcon fontSize="small" />
                    <Typography variant="body2">
                      {t("boProducts:customersPay")} <b>{formatCurrency(previewSalePrice)}</b>{" "}
                      <Box component="span" sx={{ textDecoration: "line-through", color: "text.secondary" }}>
                        {formatCurrency(previewPrice)}
                      </Box>{" "}
                      (−{previewDiscountPercent}%)
                    </Typography>
                  </Stack>
                )}
              </FormSection>

              {/* Category attributes */}
              {(loadingAttributes || descriptiveAttributes.length > 0) && (
                <FormSection
                  icon={TuneRoundedIcon}
                  title={t("boProducts:categoryAttributes")}
                  subtitle={t("boProducts:sectionAttributesHint")}
                >
                  {loadingAttributes ? (
                    <Grid container spacing={2}>
                      {[0, 1].map((i) => (
                        <Grid key={i} size={{ xs: 12, sm: 6 }}>
                          <Skeleton variant="rounded" height={40} />
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Grid container spacing={2}>
                      {descriptiveAttributes.map((attr) => (
                        <Grid
                          key={attr.attributeId}
                          size={attr.inputType === ATTRIBUTE_INPUT_DROPDOWN ? 12 : { xs: 12, sm: 6 }}
                        >
                          {attr.inputType === ATTRIBUTE_INPUT_DROPDOWN ? (
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
                                {getAttributeLabel(attr, i18n.language)}
                                {attr.unit ? ` (${attr.unit})` : ""}
                                {attr.isRequired ? " *" : ""}
                              </Typography>
                              <AttributeOptionPicker
                                attribute={attr}
                                selectedOptionId={attributeValues[attr.attributeId]?.optionId ?? null}
                                onSelect={(optionId) => setAttributeOptionValue(attr.attributeId, optionId)}
                                onAddCustom={addCustomAttributeOption}
                              />
                            </Box>
                          ) : (
                            <TextField
                              label={`${getAttributeLabel(attr, i18n.language)}${attr.unit ? ` (${attr.unit})` : ""}${attr.isRequired ? " *" : ""}`}
                              type={attr.inputType === ATTRIBUTE_INPUT_NUMBER ? "number" : "text"}
                              value={attributeValues[attr.attributeId]?.textValue ?? ""}
                              onChange={(e) => setAttributeTextValue(attr.attributeId, e.target.value)}
                              size="small"
                              fullWidth
                            />
                          )}
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </FormSection>
              )}

              {/* Variants */}
              <FormSection
                icon={StyleOutlinedIcon}
                title={t("boProducts:variants")}
                subtitle={t("boProducts:variantsHint")}
                action={
                  !showVariantForm && (
                    <Button size="small" variant="outlined" startIcon={<AddRoundedIcon />} onClick={openAddVariant}>
                      {t("boProducts:addVariant")}
                    </Button>
                  )
                }
              >
                <Stack spacing={1.25}>
                  {variants.length + pendingVariants.length === 0 && !showVariantForm && (
                    <Box
                      sx={{
                        py: 3,
                        px: 2,
                        textAlign: "center",
                        borderRadius: 2,
                        border: "1.5px dashed",
                        borderColor: "divider",
                        color: "text.secondary",
                      }}
                    >
                      <StyleOutlinedIcon sx={{ fontSize: 28, opacity: 0.5 }} />
                      <Typography variant="body2">{t("boProducts:noVariants")}</Typography>
                    </Box>
                  )}

                  {visibleVariants.map((v) => (
                    <Stack key={v.variantId} direction="row" spacing={1.5} alignItems="center" sx={variantRowSx}>
                      <Box sx={variantThumbSx}>
                        {busyVariantId === v.variantId ? (
                          <CircularProgress size={16} />
                        ) : v.imageUrl ? (
                          <Box component="img" src={v.imageUrl} alt="" sx={{ width: "100%", height: "100%", objectFit: "contain" }} />
                        ) : (
                          <PhotoCameraOutlinedIcon sx={{ fontSize: 18, color: "text.disabled" }} />
                        )}
                      </Box>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap title={v.variantName || t("boProducts:variantName")}>
                            {v.variantName || t("boProducts:variantName")}
                          </Typography>
                          {v.pendingBackgroundRemoval && (
                            <Tooltip title={t("boProducts:backgroundRemovalOnSave")}>
                              <AutoFixHighRoundedIcon sx={{ fontSize: 14, color: "info.main" }} />
                            </Tooltip>
                          )}
                          {!v.isActive && (
                            <Chip size="small" variant="outlined" label={t("boProducts:variantInactive")} sx={{ height: 20, fontSize: 11 }} />
                          )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {v.discountPrice != null ? (
                            <>
                              <Box component="span" sx={{ textDecoration: "line-through", mr: 0.5 }}>
                                {formatCurrency(v.price)}
                              </Box>
                              <Box component="span" sx={{ color: "error.main", fontWeight: 600 }}>
                                {formatCurrency(v.discountPrice)}
                              </Box>
                            </>
                          ) : (
                            formatCurrency(v.price)
                          )}
                          {" · "}
                          {t("boProducts:inStockCount", { count: v.stockQuantity })}
                          {v.sku ? ` · ${v.sku}` : ""}
                        </Typography>
                      </Box>
                      {v.imageUrl && (
                        <Tooltip title={t("boProducts:removeBackground")}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveVariantImageBackground(v.variantId)}
                              disabled={busyVariantId === v.variantId}
                            >
                              <AutoFixHighRoundedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {v.imageUrl && (
                        <Tooltip title={t("boProducts:removeVariantPhoto")}>
                          <span>
                            <IconButton size="small" onClick={() => handleDeleteVariantImage(v.variantId)} disabled={busyVariantId === v.variantId}>
                              <CloseRoundedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip title={t("common:edit")}>
                        <IconButton size="small" onClick={() => openEditVariant(v)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("common:delete")}>
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => confirmDeleteVariant(v.variantId)}
                            disabled={busyVariantId === v.variantId}
                          >
                            <DeleteOutlineRoundedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  ))}

                  {pendingVariants.map((pv, index) => (
                    <Stack key={index} direction="row" spacing={1.5} alignItems="center" sx={variantRowSx}>
                      <Box sx={variantThumbSx}>
                        {pv.imagePreviewUrl ? (
                          <Box component="img" src={pv.imagePreviewUrl} alt="" sx={{ width: "100%", height: "100%", objectFit: "contain" }} />
                        ) : (
                          <PhotoCameraOutlinedIcon sx={{ fontSize: 18, color: "text.disabled" }} />
                        )}
                      </Box>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap title={pv.variantName || t("boProducts:variantName")}>
                          {pv.variantName || t("boProducts:variantName")}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {pv.discountPrice != null ? (
                            <>
                              <Box component="span" sx={{ textDecoration: "line-through", mr: 0.5 }}>
                                {formatCurrency(pv.price)}
                              </Box>
                              <Box component="span" sx={{ color: "error.main", fontWeight: 600 }}>
                                {formatCurrency(pv.discountPrice)}
                              </Box>
                            </>
                          ) : (
                            formatCurrency(pv.price)
                          )}
                          {" · "}
                          {t("boProducts:inStockCount", { count: pv.stockQuantity })}
                          {pv.sku ? ` · ${pv.sku}` : ""}
                        </Typography>
                      </Box>
                      <Tooltip title={t("common:edit")}>
                        <IconButton size="small" onClick={() => openEditPendingVariant(index)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("common:delete")}>
                        <IconButton size="small" color="error" onClick={() => removePendingVariant(index)}>
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ))}

                  {showVariantForm && (
                    <Stack
                      spacing={2}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: "1.5px solid",
                        borderColor: "primary.main",
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <Box sx={{ position: "relative", width: 72, height: 72, flexShrink: 0 }}>
                          <Box
                            component="label"
                            sx={{
                              width: 72,
                              height: 72,
                              borderRadius: 2,
                              border: "1.5px dashed",
                              borderColor: "divider",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              overflow: "hidden",
                              bgcolor: "background.paper",
                              "&:hover": { borderColor: "primary.main" },
                            }}
                          >
                            {variantForm.imagePreviewUrl ? (
                              <Box component="img" src={variantForm.imagePreviewUrl} alt="" sx={{ width: "100%", height: "100%", objectFit: "contain" }} />
                            ) : (
                              <PhotoCameraOutlinedIcon sx={{ fontSize: 22, color: "text.secondary" }} />
                            )}
                            <input type="file" accept="image/*" hidden onChange={handleVariantImageFile} />
                          </Box>
                          {processingVariantFormBackground && (
                            <Box sx={{ ...mediaTileBusySx, borderRadius: 2 }}>
                              <CircularProgress size={16} sx={{ color: "#fff" }} />
                            </Box>
                          )}
                          <Stack direction="row" spacing={0.25} sx={{ position: "absolute", top: 2, insetInlineEnd: 2 }}>
                            <Tooltip title={t("boProducts:addFromLink")}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setVariantImageLinkDialogOpen(true);
                                }}
                                sx={{ bgcolor: "rgba(0,0,0,0.5)", color: "#fff", p: 0.3, "&:hover": { bgcolor: "rgba(0,0,0,0.7)" } }}
                              >
                                <InsertLinkRoundedIcon sx={{ fontSize: 12 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t("boProducts:pasteImage")}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handlePasteVariantImageFromClipboard();
                                }}
                                sx={{ bgcolor: "rgba(0,0,0,0.5)", color: "#fff", p: 0.3, "&:hover": { bgcolor: "rgba(0,0,0,0.7)" } }}
                              >
                                <ContentPasteGoRoundedIcon sx={{ fontSize: 12 }} />
                              </IconButton>
                            </Tooltip>
                            {variantForm.imagePreviewUrl && (
                              <Tooltip title={t("boProducts:removeBackground")}>
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleRemoveVariantFormImageBackground();
                                    }}
                                    disabled={processingVariantFormBackground || variantForm.removeBackground}
                                    sx={{ bgcolor: "rgba(0,0,0,0.5)", color: "#fff", p: 0.3, "&:hover": { bgcolor: "rgba(0,0,0,0.7)" } }}
                                  >
                                    <AutoFixHighRoundedIcon sx={{ fontSize: 12 }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </Stack>
                        </Box>

                        <Stack spacing={1} sx={{ flexGrow: 1, minWidth: 0 }}>
                          {variantAttributes.map((attr) => (
                            <Box key={attr.attributeId}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                {getAttributeLabel(attr, i18n.language)}
                                {attr.unit ? ` (${attr.unit})` : ""} *
                              </Typography>
                              <AttributeOptionPicker
                                attribute={attr}
                                selectedOptionId={variantForm.attributeValues[attr.attributeId] ?? null}
                                onSelect={(optionId) => setVariantAttributeOption(attr.attributeId, optionId)}
                                onAddCustom={addCustomAttributeOption}
                              />
                            </Box>
                          ))}
                          <TextField
                            label={variantAttributes.length > 0 ? t("boProducts:variantNameOptionalAuto") : t("boProducts:variantName")}
                            value={variantForm.variantName}
                            onChange={(e) => setVariantForm((prev) => ({ ...prev, variantName: e.target.value }))}
                            size="small"
                            fullWidth
                            sx={fieldOnTintSx}
                          />
                        </Stack>
                      </Stack>

                      <Grid container spacing={1.5}>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <TextField
                            label={t("common:price")}
                            type="number"
                            value={variantForm.price}
                            onChange={(e) => setVariantForm((prev) => ({ ...prev, price: e.target.value }))}
                            size="small"
                            fullWidth
                            sx={fieldOnTintSx}
                            slotProps={{ input: { startAdornment: <InputAdornment position="start">₪</InputAdornment> } }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <TextField
                            label={t("boProducts:stockQuantity")}
                            type="number"
                            value={variantForm.stockQuantity}
                            onChange={(e) => setVariantForm((prev) => ({ ...prev, stockQuantity: e.target.value }))}
                            size="small"
                            fullWidth
                            sx={fieldOnTintSx}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <TextField
                            label={t("boProducts:skuOptional")}
                            value={variantForm.sku}
                            onChange={(e) => setVariantForm((prev) => ({ ...prev, sku: e.target.value }))}
                            size="small"
                            fullWidth
                            sx={fieldOnTintSx}
                          />
                        </Grid>
                      </Grid>

                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }}>
                        <ToggleButtonGroup
                          exclusive
                          size="small"
                          color="primary"
                          value={variantForm.discountType}
                          onChange={(_, value) => value != null && setVariantForm((prev) => ({ ...prev, discountType: value }))}
                          sx={{ ...discountToggleSx, bgcolor: "background.paper" }}
                        >
                          <ToggleButton value={DISCOUNT_TYPE_NONE}>{t("boProducts:discountNone")}</ToggleButton>
                          <ToggleButton value={DISCOUNT_TYPE_FIXED}>{t("boProducts:discountFixed")}</ToggleButton>
                          <ToggleButton value={DISCOUNT_TYPE_PERCENTAGE}>{t("boProducts:discountPercentage")}</ToggleButton>
                        </ToggleButtonGroup>
                        {variantForm.discountType === DISCOUNT_TYPE_FIXED && (
                          <TextField
                            label={t("boProducts:salePrice")}
                            type="number"
                            value={variantForm.discountPrice}
                            onChange={(e) => setVariantForm((prev) => ({ ...prev, discountPrice: e.target.value }))}
                            size="small"
                            sx={{ ...fieldOnTintSx, flexGrow: 1 }}
                            slotProps={{ input: { startAdornment: <InputAdornment position="start">₪</InputAdornment> } }}
                          />
                        )}
                        {variantForm.discountType === DISCOUNT_TYPE_PERCENTAGE && (
                          <TextField
                            label={t("boProducts:discountPercentLabel")}
                            type="number"
                            value={variantForm.discountPercent}
                            onChange={(e) => setVariantForm((prev) => ({ ...prev, discountPercent: e.target.value }))}
                            size="small"
                            sx={{ ...fieldOnTintSx, flexGrow: 1 }}
                            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
                          />
                        )}
                      </Stack>

                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button onClick={cancelVariantForm} color="inherit">
                          {t("common:cancel")}
                        </Button>
                        <Button variant="contained" disableElevation onClick={saveVariant}>
                          {editingVariantId || editingPendingVariantIndex != null
                            ? t("boProducts:saveVariant")
                            : t("boProducts:addVariant")}
                        </Button>
                      </Stack>
                    </Stack>
                  )}
                </Stack>
              </FormSection>
            </Stack>
          </Grid>

          {/* ---------- Side column ---------- */}
          <Grid size={{ xs: 12, md: 4 }} sx={{ order: { xs: 1, md: 2 } }}>
            <Stack spacing={{ xs: 2, md: 3 }}>
              {isEditing && (
                <FormSection icon={VisibilityOutlinedIcon} title={t("boProducts:sectionStatus")}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{
                      px: 1.5,
                      py: 1,
                      borderRadius: 2,
                      bgcolor: (theme) => alpha(isActive ? theme.palette.success.main : theme.palette.text.primary, isActive ? 0.08 : 0.04),
                    }}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: isActive ? "success.dark" : "text.secondary" }}>
                        {isActive ? t("boShared:active") : t("boShared:inactive")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("boProducts:inactiveHint")}
                      </Typography>
                    </Box>
                    <Switch color="success" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                  </Stack>
                </FormSection>
              )}

              <FormSection icon={CategoryOutlinedIcon} title={t("boProducts:sectionOrganization")}>
                <Stack spacing={2}>
                  <TextField
                    select
                    required
                    size="small"
                    fullWidth
                    label={t("boProducts:category")}
                    value={categoryId ?? ""}
                    onChange={(e) => setCategoryId(e.target.value)}
                    disabled={loadingCategories || categoryOptions.length === 0}
                    helperText={!loadingCategories && categoryOptions.length === 0 ? t("boProducts:noCategoriesHint") : undefined}
                  >
                    {categoryOptions.map((c) => (
                      <MenuItem key={c.categoryId} value={c.categoryId}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    label={t("boProducts:authenticity")}
                    value={authenticityOptionId ?? ""}
                    onChange={(e) => setAuthenticityOptionId(e.target.value === "" ? null : Number(e.target.value))}
                    slotProps={{ inputLabel: { shrink: true }, select: { displayEmpty: true } }}
                  >
                    <MenuItem value="">
                      <Typography component="span" color="text.secondary">
                        {t("boProducts:notSpecified")}
                      </Typography>
                    </MenuItem>
                    {authenticityOptions.map((option) => (
                      <MenuItem key={option.authenticityOptionId} value={option.authenticityOptionId}>
                        {option.value}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
              </FormSection>

              <FormSection icon={LocalShippingOutlinedIcon} title={t("boProducts:sectionShipping")}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {t("boProducts:requiresSpecialDelivery")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("boProducts:specialDeliveryHint")}
                    </Typography>
                  </Box>
                  <Switch checked={requiresSpecialDelivery} onChange={(e) => setRequiresSpecialDelivery(e.target.checked)} />
                </Stack>
              </FormSection>

              {/* Live customer preview (desktop only - on phones it would push the form down) */}
              <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: "hidden", display: { xs: "none", md: "block" } }}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ px: 2, py: 1.25, borderBottom: "1px solid", borderColor: "divider" }}
                >
                  <StorefrontOutlinedIcon fontSize="small" color="action" />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}
                  >
                    {t("boProducts:customerPreview")}
                  </Typography>
                </Stack>
                <Box sx={{ p: 2, opacity: isActive ? 1 : 0.55 }}>
                  <Box
                    sx={{
                      position: "relative",
                      aspectRatio: "4 / 3",
                      borderRadius: 2,
                      bgcolor: "background.default",
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 1.5,
                    }}
                  >
                    {previewImageUrl ? (
                      <Box component="img" src={previewImageUrl} alt="" sx={{ width: "100%", height: "100%", objectFit: "contain" }} />
                    ) : (
                      <PhotoCameraOutlinedIcon sx={{ fontSize: 40, color: "text.disabled" }} />
                    )}
                    {previewDiscountPercent != null && (
                      <Chip
                        size="small"
                        color="error"
                        label={`−${previewDiscountPercent}%`}
                        sx={{ position: "absolute", top: 8, insetInlineStart: 8, fontWeight: 700 }}
                      />
                    )}
                    {selectedPhotoUrl && selectedPhotoUrl !== mainImageUrl && (
                      <Chip
                        size="small"
                        label={t("boProducts:showMainPhoto")}
                        onClick={() => setSelectedPhotoKey(null)}
                        onDelete={() => setSelectedPhotoKey(null)}
                        sx={{ position: "absolute", bottom: 8, insetInlineEnd: 8, bgcolor: "rgba(0,0,0,0.6)", color: "#fff", "& .MuiChip-deleteIcon": { color: "#fff" } }}
                      />
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      color: name.trim() ? "text.primary" : "text.disabled",
                    }}
                  >
                    {name.trim() || t("boProducts:name")}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mt: 0.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: previewSalePrice != null ? "error.main" : "text.primary" }}>
                      {hasValidPrice ? formatCurrency(previewSalePrice ?? previewPrice) : "—"}
                    </Typography>
                    {previewSalePrice != null && (
                      <Typography variant="caption" color="text.secondary" sx={{ textDecoration: "line-through" }}>
                        {formatCurrency(previewPrice)}
                      </Typography>
                    )}
                  </Stack>
                  {isOutOfStock && (
                    <Typography variant="caption" sx={{ color: "warning.main", fontWeight: 600 }}>
                      {t("boProducts:outOfStock")}
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Stack>
          </Grid>
        </Grid>

        <AddImageByUrlDialog
          open={imageLinkDialogOpen}
          onClose={() => setImageLinkDialogOpen(false)}
          onAdd={handleAddImageFromUrl}
          busy={false}
        />
        <AddImageByUrlDialog
          open={variantImageLinkDialogOpen}
          onClose={() => setVariantImageLinkDialogOpen(false)}
          onAdd={handleVariantImageFromUrl}
          busy={false}
        />
      </DialogContent>

      {/* ---------- Footer (always visible, so errors show next to the button that caused them) ---------- */}
      <DialogActions
        disableSpacing
        sx={{
          px: { xs: 2, sm: 3 },
          py: 1.5,
          gap: 1.5,
          flexWrap: "wrap",
          bgcolor: "background.paper",
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ flex: "1 1 260px", minWidth: 0 }}>
          {formError ? (
            <Alert severity="error" onClose={() => setFormError("")} sx={{ py: 0, borderRadius: 2 }}>
              {formError}
            </Alert>
          ) : storeRequiresReview && isNewUnsaved ? (
            <Typography variant="caption" color="text.secondary">
              {t("boProducts:sendForReviewHint")}
            </Typography>
          ) : (
            <Typography variant="caption" color="text.disabled" sx={{ display: { xs: "none", sm: "block" } }}>
              {t("boProducts:saveShortcut")}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0, marginInlineStart: "auto" }}>
          <Button onClick={onClose} disabled={saving} color="inherit">
            {t("common:cancel")}
          </Button>
          <Button
            variant={secondaryAction ? "outlined" : "contained"}
            disableElevation
            disabled={!canSubmit}
            onClick={() => handleSubmit()}
            startIcon={
              saving ? (
                <CircularProgress size={16} color="inherit" />
              ) : isRejected || (storeRequiresReview && isNewUnsaved) ? (
                <SendRoundedIcon />
              ) : undefined
            }
            sx={{ px: 2.5 }}
          >
            {submitLabel}
          </Button>
          {secondaryAction && isEditing && (
            <Button
              variant="contained"
              disableElevation
              color={secondaryAction.color || "primary"}
              disabled={!canSubmit}
              onClick={() => handleSubmit(secondaryAction.onAfterSave)}
              sx={{ px: 2.5 }}
            >
              {secondaryAction.label}
            </Button>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
